import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useLocalization } from '@progress/kendo-react-intl';
import siteExplorerApiService from "../../../Utilities/SiteExplorerApiService";
import { EventFilter } from "../../../../Events/Utilities/EventsApiService";
import { useQuery } from "react-query";
import LoadingSpinner from "../../../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../../../Shared/Components/Cards/ErrorCard";
import NoDataPageTab from "../../NoDataPageTab";
import moment from "moment";
import { enMessages } from "../../../../../messages/en-US";
import EndpointPressureEventsDto from "../../../../../Models/Endpoint/EndpointPressureEventsDto";
import EndpointStatusChart from "../EndpointStatusChart";
import { ChartToolbar } from "../../../Utilities/ChartToolbar";
import useChartStore from "../../../../../Zustand/chartStore";
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from "@progress/kendo-react-layout";
import EndpointEventTable from "../../../Components/Endpoints/EndpointEventsTable";
import EndpointPressureEventsTable from "../../../Components/Endpoints/EndpointPressureEventsTable";
import EndpointTemperatureEventsTable from "../../../Components/Endpoints/EndpointTemperatureEventsTable";
import EndpointDiagnosticsTable from "../../../Components/Endpoints/EndpointDiagnosticsTable";
import EndpointMetricsTable from "../../../Components/Endpoints/EndpointMetricsTable";
import EndpointGreasePumpEventsTable from "../../../Components/Endpoints/EndpointGreasePumpEventsTable";
import EndpointPressureEventsTableDto from "../../../../../Models/Endpoint/EndpointPressureEventsTableDto";
import EndpointTemperatureEventsTableDto from "../../../../../Models/Endpoint/EndpointTemperatureEventsTableDto";
import { StateEventsReadDto } from "../../../../../Models/State/StateEventsReadDto";
import assetsDashboardApiService from "../../../../Dashboards/Assets/Utilities/AssetsDashboardApiService";

export interface EndpointDiagnosticsTabPageProps {
  eventFilter: EventFilter
  selectedItem: string
  selectedEndpointId: string;
  endpointFeature: any;
  showTables: boolean;
  isSimpleChart: boolean;
  showChartTools?: boolean;
  chartParentHeight?: number;
  isDiagnostics?: boolean;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
  showLegendOverride?: boolean;
  showStatus?: boolean;
  /** When set (e.g. from dashboard), avoids refetch when switching tabs */
  queryStaleTime?: number;
}

// Componente interno para renderizar tabelas de Diagnostics
function DiagnosticsTablesView(props: {
  eventFilter: EventFilter;
  selectedItem: string;
  aggregator: string;
  endpointFeature: any;
  diagnosticsData: any;
  metricsData: any;
  endpointSerialNo: string;
  showGreasePump?: boolean;
}) {
  const localizationService = useLocalization();
  const [selected, setSelected] = useState<number>(0);

  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelected(e.selected);
  };

  const greasePumpEventsTableQuery = useQuery<StateEventsReadDto[]>([`greasePumpEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter.startDate, props.eventFilter.endDate], async () => {
    return assetsDashboardApiService.getEndpointEventsState(props.selectedItem, props.eventFilter.startDate!, props.eventFilter.endDate!);
  }, { enabled: !!props.showGreasePump && !!props.eventFilter.startDate && !!props.eventFilter.endDate });

  const endpointsQueriesTable = useQuery<any>(['endpointTable', props.selectedItem, props.eventFilter, props.aggregator], 
    async () => siteExplorerApiService.getAllEndpointsEventsFlowTable(props.eventFilter, props.selectedItem, props.aggregator), 
    {enabled: selected === 0} 
  );

  const endpointPressureEventsTableQuery = useQuery<EndpointPressureEventsTableDto[]>([`endpointPressureEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, props.aggregator], async () => {
    return siteExplorerApiService.getEndpointPressureEventsTable(props.selectedItem, props.eventFilter, props.aggregator) 
  }, {enabled: selected === 1});

  const endpointTemperatureEventsTableQuery = useQuery<EndpointTemperatureEventsTableDto[]>([`endpointTemperatureEventsTableQuery${props.selectedItem}`, props.selectedItem, props.eventFilter, props.aggregator], async () => {
    return siteExplorerApiService.getEndpointTemperatureEventsTable(props.selectedItem, props.eventFilter, 'none') 
  }, {enabled: selected === 2});

  if (endpointPressureEventsTableQuery.isLoading || endpointsQueriesTable.isLoading || endpointTemperatureEventsTableQuery.isLoading || (props.showGreasePump && (greasePumpEventsTableQuery.isLoading || greasePumpEventsTableQuery.isFetching))) {
    return <LoadingSpinner />;
  } else if (endpointPressureEventsTableQuery.isError || endpointsQueriesTable.isError || endpointTemperatureEventsTableQuery.isError || greasePumpEventsTableQuery.isError) {
    return <ErrorCard />;
  } else {
    return (
      <div className="mt-5" >
        <TabStrip selected={selected} onSelect={handleSelect} className="h-full">
          <TabStripTab title={localizationService.toLanguageString('custom.flow', enMessages.custom.flow)} >
            <EndpointEventTable events={endpointsQueriesTable.data!} aggregator={props.aggregator} endpointSerialNo={props.endpointSerialNo}/>
          </TabStripTab>
          {props.endpointFeature.isPressureSupported && 
            <TabStripTab title={localizationService.toLanguageString('custom.pressure', enMessages.custom.pressure)} >
              <EndpointPressureEventsTable events={endpointPressureEventsTableQuery.data!} aggregator={props.aggregator} endpointSerialNo={props.endpointSerialNo}/>   
            </TabStripTab>
          }
          {props.endpointFeature.isPressureSupported && 
            <TabStripTab title={localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature)} >
              <EndpointTemperatureEventsTable events={endpointTemperatureEventsTableQuery.data!} aggregator={'none'}/>   
            </TabStripTab>
          }
          <TabStripTab title={localizationService.toLanguageString('custom.diagnostic', enMessages.custom.diagnostic)} >
            <EndpointDiagnosticsTable events={props.diagnosticsData} aggregator={props.aggregator} endpointSerialNo={props.endpointSerialNo}/>
          </TabStripTab>
          <TabStripTab title={localizationService.toLanguageString('custom.heartbeat', enMessages.custom.heartbeat)} >
            <EndpointMetricsTable events={props.metricsData} aggregator={props.aggregator} endpointSerialNo={props.endpointSerialNo}/>
          </TabStripTab>
          {props.showGreasePump &&
            <TabStripTab title={localizationService.toLanguageString('custom.greasePump', enMessages.custom.greasePump)} >
              <EndpointGreasePumpEventsTable events={greasePumpEventsTableQuery.data ?? []} endpointSerialNo={props.endpointSerialNo}/>
            </TabStripTab>
          }
        </TabStrip>
      </div>
    );
  }
}

export default function EndpointDiagnosticsTabPage(props: EndpointDiagnosticsTabPageProps): ReactElement {
  const localizationService = useLocalization();

  const aggregator = useChartStore((state) => state.aggregator);

  const viewType = useChartStore((state) => state.viewType);

  const setViewType = useChartStore((state) => state.setViewType);

  const setAggregator = useChartStore((state) => state.setAggregator);

  const setVisibleRange = useChartStore((state) => state.setVisibleRange);

  const setAutoRange = useChartStore((state) => state.setAutoRange);
  
  const queryOptions = props.queryStaleTime != null ? { staleTime: props.queryStaleTime } : {};

  const endpointsDiagnosticsQuery = useQuery<any>(["Diagnostics", props.eventFilter, props.selectedItem], async () => {
    return siteExplorerApiService.getAllEndpointsDiagnosticsChart(props.eventFilter, props.selectedItem);
  }, queryOptions);

  const endpointsQuery = useQuery<any>(['endpointChart', props.selectedEndpointId, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getAllEndpointsEventsFlowChart(props.eventFilter, props.selectedEndpointId, aggregator) 
  }, queryOptions);

  // Sort on a copy — sorting the query data in place mutates the React Query cache
  const diagnosticsChart = useMemo(() => {
    return endpointsDiagnosticsQuery.data?.slice().sort((a,b) => a.timestamp > b.timestamp ? 1 : -1);
  }, [endpointsDiagnosticsQuery.data]);

  const endpointsMetricsQuery = useQuery<any>(["Metrics", props.eventFilter, props.selectedItem], async () => {
    return siteExplorerApiService.getAllEndpointsMetricsChart(props.eventFilter, props.selectedItem);
  }, queryOptions);
  const metricsChart = useMemo(() => {
    return endpointsMetricsQuery.data?.slice().sort((a,b) => a.timestamp > b.timestamp ? 1 : -1);
  }, [endpointsMetricsQuery.data]);

  const notificationsQuery = useQuery<any>(
    ["notificationsStatus", props.selectedEndpointId, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointNotifications(props.selectedEndpointId, props.eventFilter, 1);
    },
    queryOptions
  );

  const statusQuery = useQuery<any>(
    ["statusChart", props.selectedEndpointId, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointStatus(props.selectedEndpointId, props.eventFilter, 0);
    },
    { ...queryOptions, enabled: !!props.selectedEndpointId }
  );

  const endpointTemperatureEventsChartQuery = useQuery<any>([`endpointTemperatureEventsChartQuery${props.selectedEndpointId}`, props.selectedEndpointId, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getEndpointTemperatureEventsChart(props.selectedEndpointId, props.eventFilter, 'none') 
  }, { ...queryOptions, enabled: props.endpointFeature.isTemperatureSupported });

  const isFirstMountRef = useRef(true);
  useEffect(() => {
    if (props.queryStaleTime != null && isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    isFirstMountRef.current = false;
    endpointsDiagnosticsQuery.refetch();
    endpointsMetricsQuery.refetch();
    endpointsQuery.refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedItem, props.eventFilter])

  const endpointPressureEventsChartQuery = useQuery<EndpointPressureEventsDto>([`endpointPressureEventsChartQuery`, props.selectedEndpointId, props.eventFilter, aggregator], async () => {
    return  siteExplorerApiService.getEndpointPressureEventsChart(props.selectedEndpointId, props.eventFilter, aggregator) 
  }, { ...queryOptions, enabled: props.endpointFeature.isPressureSupported });

  const diagnosticsDataChart = useMemo(() => {
    const temperature = diagnosticsChart?.map(x => x.temperature)
    const voltage = diagnosticsChart?.map(x => x.voltage/1000)
    const battery = diagnosticsChart?.map(x => x.coulombCount)
    const rssi = metricsChart?.map(x => x.rssi)
    const endpointRssi = metricsChart?.map(x => x.endpointRssi)
    const cumulative = metricsChart?.map(x => x.kFactor * x.cumulative )

    return {
      statusChart: {
        dateFilter: props.eventFilter, timestamps: endpointsQuery.data?.timestamps, alerts: notificationsQuery.data, statusData: statusQuery.data, },

      diagnosticsChart: {
        timestampsDiagnostics: diagnosticsChart?.map(x => Math.floor(moment(x.timestamp).valueOf()/1000)),
        temperatureData: temperature?.map(item => item === 'NaN' ? NaN :  item) !== undefined ? temperature?.map(item => item === 'NaN' ? NaN :  item) :[],
        voltageData:  voltage?.map(item => item === 'NaN' ? NaN :  item) !== undefined ? voltage?.map(item => item === 'NaN' ? NaN :  item) : [],
        batteryData: battery?.map(item => item == null || item === 'NaN' ? NaN : item) ?? [],
        batterySlots: diagnosticsChart?.map(x => x.slot) ?? [],
        timestampsMetrics: metricsChart?.map(x => Math.floor(moment(x.timestamp).valueOf()/1000)),
        rssiData:  rssi?.map(item => item === 'NaN' ? NaN :  item) !== undefined ? rssi?.map(item => item === 'NaN' ? NaN :  item) : [],
        endpointRssiData:  endpointRssi?.map(item => item === 'NaN' ? NaN :  item) !== undefined ? endpointRssi?.map(item => item === 'NaN' ? NaN :  item) : [],
        cumulativeData:  cumulative?.map(item => item === 'NaN' ? NaN :  item) !==undefined ? cumulative?.map(item => item === 'NaN' ? NaN :  item) : [],
        dateFilter: props.eventFilter,
        timestamps: endpointsQuery.data?.timestamps,
        volumeDispensedData: endpointsQuery.data?.volumeDispensedData,
        strokeThickness: 1,
        aggregator: aggregator,
        pressureEvents: endpointPressureEventsChartQuery.data!,
        temperatureEvents: endpointTemperatureEventsChartQuery.data!,
      },
    };
    // The temperature query must be a dependency: the chart snapshots chartData on mount, so
    // if it resolves last without recomputing here the temperature series is never created.
  },[diagnosticsChart, metricsChart, endpointsQuery.data, endpointPressureEventsChartQuery.data, endpointTemperatureEventsChartQuery.data, statusQuery.data, notificationsQuery.data, aggregator, props.eventFilter]);

  const metricsTableData = useMemo(() => {
    return metricsChart?.map((item) => ({
      ...item,
      cumulative: Number.isNaN((item.kFactor * item.cumulative)) ? '-' : (item.kFactor * item.cumulative).toFixed(2),
    }));
  }, [metricsChart]);

  useEffect(() => {
    if (viewType === 'bar') {
      setViewType('line');
    }
  }, [viewType, setViewType]);

  useEffect(() => {
    if (props.isSimpleChart) {
      setViewType('line');
      setAggregator('none');
      setVisibleRange(moment(props.eventFilter.startDate).unix(), moment(props.eventFilter.endDate).unix());
      setAutoRange(true);
    }
    if(!props.isDiagnostics){
      setViewType('line');
    }
  }, [props.isSimpleChart, setViewType, setAggregator, setVisibleRange, props.eventFilter, setAutoRange, props.isDiagnostics]);

  if(props.selectedItem === "") {
    return <div className="text-center"><NoDataPageTab/></div>
  } else {
  if (endpointsDiagnosticsQuery.isLoading || endpointsMetricsQuery.isLoading || endpointsQuery.isLoading || endpointPressureEventsChartQuery.isLoading || endpointTemperatureEventsChartQuery.isLoading || notificationsQuery.isLoading || statusQuery.isLoading || endpointsDiagnosticsQuery.isFetching || endpointsMetricsQuery.isFetching || endpointsQuery.isFetching || endpointPressureEventsChartQuery.isFetching || endpointTemperatureEventsChartQuery.isFetching || notificationsQuery.isFetching || statusQuery.isFetching) {
    return <div className="flex items-center justify-center h-full w-full">
      <LoadingSpinner />
    </div>;
  } else {
    if (endpointsDiagnosticsQuery.error || endpointsMetricsQuery.error || endpointsQuery.error || endpointPressureEventsChartQuery.error || endpointTemperatureEventsChartQuery.error || notificationsQuery.error || statusQuery.error) {
      return <ErrorCard />;
    } else { 
    return (
      <div className="text-center h-full">
      {(endpointsMetricsQuery.data?.length > 0 || endpointsDiagnosticsQuery.data?.length > 0) ?
      <div>
        {(!props.isSimpleChart && props.isDiagnostics !== false && props.showChartTools !== false) && (
          <ChartToolbar 
            title={localizationService.toLanguageString('custom.events', enMessages.custom.events)}
            lastSeen={null}
            showAutoRange={false} 
            showAggregatorOptions={true} 
            showChartOptions={true}
            showThresholds={false} 
            showSingleMultipleChart={false}
            showTable={true}
            showBothCharts={false}
        />
        
        )}
        {viewType !== 'table' && (
          <>
           <EndpointStatusChart
                chartType="diagnostics"
                key={props.selectedEndpointId} 
                statusChartData={diagnosticsDataChart.statusChart} 
                diagnosticsDataChart={diagnosticsDataChart.diagnosticsChart} 
                isSimpleChart={props.isSimpleChart} 
                chartParentHeight={props.chartParentHeight}
                greasePumpData={props.greasePumpData}
                showLegendOverride={props.showLegendOverride}
                showStatus={props.showStatus}
              />
          </>
        )}
        {viewType === 'table' &&
          <DiagnosticsTablesView eventFilter={props.eventFilter} selectedItem={props.selectedEndpointId} aggregator={aggregator} endpointFeature={props.endpointFeature} diagnosticsData={diagnosticsChart!} metricsData={metricsTableData} endpointSerialNo={props.selectedItem} showGreasePump={!!props.greasePumpData}/>
        }
      </div>
      :
      <div className="h-full overflow-y-auto">
        <NoDataPageTab/>
      </div>
    }
    </div>
    )
}}}}
